import moment = require("moment");
import * as React from "react";
import {Title} from "../../../../../../components/Title";
import {I18n} from "../../../../../../components/Utils/i18n";

import {DashboardPopupController, JSONCoffeeMachineMaintenanceTexts} from "../../CoffeeMachineDashboard";

import "./MaintenanceFormStyle.scss";
import {MaintenanceService} from "./MaintenanceService";
import {NewMaintenanceFormData} from "./NewMaintenanceForm";
import {NewMaintenanceInfoPopup} from "./NewMaintenanceInfoPopup";

import {Loader} from "../../../../../../components/Utils/loader/Loader";
import {PercentageBar} from "../widgets/PercentageBar";
import {Buttons} from "../../../../../../components/buttons/Buttons";
import {Icon, icons, Tooltip} from "@vebox/etui-core";

export interface MaintenanceFormProps {
    texts: JSONCoffeeMachineMaintenanceTexts;
    machineGUID: string;
    showRatios: boolean;
}

export interface MaintenanceFormState {
    lastMaintenanceDate: Date;
    installationDate: Date;
    consumedCoffeeMass: number;
}

declare var LOCALE: string;

export class MaintenanceForm extends React.Component<MaintenanceFormProps, MaintenanceFormState> {

    private static linearInterpolationGreenRed(percentage: number): string {
        return "hsla(" + (120 - (percentage * 1.2)) + ", 100%, 50%, 0.7)";
    }

    // TODO: can became util function
    private static daysDifference(a: Date, b: Date): number {
        if (!a || !b) {
            return null;
        }
        const daySinceEpochA: number = Math.floor(a.getTime() / (1000 * 3600 * 24));
        const daySinceEpochB: number = Math.floor(b.getTime() / (1000 * 3600 * 24));
        return daySinceEpochA - daySinceEpochB;
    }

    private static card(title: string, value: number, total: number, side: string, showRatios: boolean) {
        return (
            <div className={"machine-maintenance-body-info-display-" + side}>
                <div className={"card"}>
                    <div className={"card-header"}>
                        <p className={"card-header-title"}>
                            {title}
                        </p>
                        {!showRatios ? null : <p className={"card-header-subtitle"}>
                            {(value ? value : "-") + " / " + total}
                        </p>}
                    </div>
                    <PercentageBar
                        value={value}
                        total={total}
                        width={"100%"}
                        height={30}
                        dynamicBarColor={MaintenanceForm.linearInterpolationGreenRed}
                    />
                </div>
            </div>
        );
    }

    private static inputFormHeader(labelText: string, date: Date) {
        return (
            <>
                <span> {labelText} </span>
                &nbsp;
                <span style={{fontWeight: 400}}> {date ? moment(date)
                    .format(I18n.getLocaleMomentFormat(LOCALE)) : " - "} </span>
            </>
        );
    }

    constructor(props) {
        super(props);
        this.state = {
            lastMaintenanceDate: null,
            consumedCoffeeMass: null,
            installationDate: null,
        };
    }

    public componentDidMount() {
        this.getAndSetLastMaintenanceDate();
    }

    public render() {
        const daysSinceLastMaintenance: number = MaintenanceForm.daysDifference(
            new Date(),
            this.state.lastMaintenanceDate ? this.state.lastMaintenanceDate : this.state.installationDate,
        );
        return (
            <>
                <div className={"machine-maintenance"}>
                    <div className={"machine-maintenance-header"}>
                        <Title title={this.props.texts.title}/>
                        <Tooltip
                            message={this.props.texts.explanation}
                            placement={"right"}
                        >
                            <Icon
                                glyph={icons.INFO_CIRCLE}
                                size={32}
                                styles={{
                                    root: {
                                        color: "var(--second-level-tabbar-active-text-color)",
                                    }
                                }}
                            />
                        </Tooltip>
                    </div>
                    {this.state.lastMaintenanceDate || this.state.installationDate ? (
                        <div className={"machine-maintenance-body"}>
                            <div className={"machine-maintenance-body-info"}>
                                <div className={"machine-maintenance-body-form-input"}>
                                    {this.inputForm()}
                                </div>
                                <div className={"machine-maintenance-body-info-display"}>
                                    {MaintenanceForm.card(
                                        this.props.texts.days,
                                        daysSinceLastMaintenance,
                                        180,
                                        "left",
                                        this.props.showRatios,
                                    )}
                                    {MaintenanceForm.card(
                                        this.props.texts.kg,
                                        this.state.consumedCoffeeMass,
                                        400,
                                        "right",
                                        this.props.showRatios,
                                    )}
                                </div>
                            </div>
                            <div className={"machine-maintenance-body-buttons"}>
                                {
                                    this.state.lastMaintenanceDate || this.state.installationDate
                                        ? (
                                            <Buttons.ButtonWithIcon
                                                icon={"./images/buttons/iconAddSmall.png"}
                                                key={"add"}
                                                label={this.props.texts.popupTexts.add}
                                                onClick={this.addNewMaintenanceDate}
                                                code={"addNewMaintenanceDate"}
                                                selected={false}
                                            />
                                        )
                                        : null
                                }
                                {
                                    this.state.lastMaintenanceDate
                                        ? (
                                            <Buttons.ButtonWithIcon
                                                onClick={this.deleteMaintenanceDate}
                                                icon={"./images/svg/icons/undo.svg"}
                                                label={this.props.texts.deletePopupTexts.revertMaintenance}
                                            />
                                        )
                                        : null
                                }
                            </div>
                        </div>
                    ) : (
                        <div className={"maintenace-loader-wrapper"}>
                            <Loader/>
                        </div>
                    )}
                </div>
            </>
        );
    }

    private inputForm() {
        return (
            <>
                {this.state.lastMaintenanceDate
                    ? MaintenanceForm.inputFormHeader(
                        this.props.texts.fromDate,
                        this.state.lastMaintenanceDate,
                    )
                    : MaintenanceForm.inputFormHeader(
                        this.props.texts.sinceInstallation,
                        this.state.installationDate,
                    )
                }
            </>
        );
    }

    private getAndSetLastMaintenanceDate() {
        MaintenanceService.getLastMaintenanceDate(this.props.machineGUID)
            .then((lastMaintenanceDate) => {
                MaintenanceService.getMassOfCoffeeConsumed(this.props.machineGUID, lastMaintenanceDate, new Date())
                    .then((consumedCoffeeMass) => {
                        this.setState({lastMaintenanceDate, consumedCoffeeMass});
                    });
            })
            .catch(() => {
                MaintenanceService.getEquipmentInstallationDate(this.props.machineGUID)
                    .then((installationDate: Date) => {
                        MaintenanceService.getMassOfCoffeeConsumed(this.props.machineGUID, installationDate, new Date())
                            .then((consumedCoffeeMass) => {
                                this.setState({installationDate, consumedCoffeeMass});
                            });
                    });
            });
    }

    private addNewMaintenanceDate = (e): void => {
        const tryAddNewDate = (info: NewMaintenanceFormData) => {
            this.setState({
                lastMaintenanceDate: null,
                installationDate: null,
            });
            MaintenanceService.addNewMaintenance(this.props.machineGUID, info)
                .then((result) => {
                    return new Date(result.signalDate);
                }).then((lastMaintenanceDate) => {
                return MaintenanceService
                    .getMassOfCoffeeConsumed(this.props.machineGUID, lastMaintenanceDate, new Date())
                    .then((consumedCoffeeMass) => {
                        this.setState({lastMaintenanceDate, consumedCoffeeMass});
                    });
            }).catch((_) => {
                this.getAndSetLastMaintenanceDate();
            });
            return true;
        };

        const onCancel = () => null;
        DashboardPopupController.showPopup((
            <NewMaintenanceInfoPopup
                onAdd={tryAddNewDate}
                onCancel={onCancel}
                texts={this.props.texts.popupTexts}
                dateInterval={{
                    start: this.state.lastMaintenanceDate
                        ? this.state.lastMaintenanceDate
                        : this.state.installationDate,
                    end: new Date(),
                }}
            />
        ));
    }

    private deleteMaintenanceDate = (e): void => {
        const tryDeleteMaintenanceDate = () => {
            this.setState({
                lastMaintenanceDate: null,
                installationDate: null,
            });
            MaintenanceService.deleteMaintenance(this.props.machineGUID)
                .then((result) => {
                    return new Date(result.signalDate);
                }).then((lastMaintenanceDate) => {
                return MaintenanceService
                    .getMassOfCoffeeConsumed(this.props.machineGUID, lastMaintenanceDate, new Date())
                    .then((consumedCoffeeMass) => {
                        this.setState({lastMaintenanceDate, consumedCoffeeMass});
                    });
            }).catch((_) => {
                this.getAndSetLastMaintenanceDate();
            });
            return true;
        };

        const onCancel = () => null;
        // const text = JSON.parse(JSON.stringify(this.props.texts.popupTexts));
        // text.add = "conferma";
        // text.dateLabel = "Vuoi cancellare l'ultimo intervento di manutenzione?";
        // text.title = "CANCELLA INTERVENTO DI MANUTENZIONE";
        DashboardPopupController.showPopup((
            <NewMaintenanceInfoPopup
                onAdd={tryDeleteMaintenanceDate}
                onCancel={onCancel}
                texts={this.props.texts.deletePopupTexts}
            />
        ));
    }
}
